/**
 * Call Follow-Up Datasource (Infrastructure)
 *
 * Handles raw API communication for call follow-up operations.
 * Maps v2 backend responses to frontend domain types.
 */

import { post, get } from '@/shared/api';

import type { V2CallResponse } from '@/shared/services/socketio/types';
import type { CallFollowUpModel } from '../../domain/types';
import type { CallFollowUpSearchRequest } from '../../domain/types/requests';
import type { CallFollowUpSearchResponse, CallFollowUpSearchOptionsResponse } from '../../domain/types/responses';

// ============================================================================
// v2 Response Mapper
// ============================================================================

/**
 * Map v2 CallResponse to CallFollowUpModel
 */
function mapV2CallToFollowUp(call: V2CallResponse): CallFollowUpModel {
  const callee = call.callees?.[0];
  return {
    id: call.id,
    loan_id: call.case_info?.loan_id,
    status: call.status,
    assigned_tier: call.case_info?.tier,
    caller: call.caller
      ? {
          id: call.caller.id,
          name: call.caller.name,
          photo_url: call.caller.photo_url,
          role: call.caller.role,
          nric: call.caller.nric,
        }
      : undefined,
    callee: callee
      ? {
          id: callee.id,
          name: callee.name,
          photo_url: callee.photo_url,
          role: callee.role,
          nric: callee.nric,
        }
      : undefined,
    num_tries: 0,
    call_queue_type: call.type === 'inbound' ? 'incoming' : 'outgoing',
    time_start: call.time_start,
    time_end: call.time_end,
    case_id: call.case_info?.case_id,
  };
}

// ============================================================================
// Datasource Interface
// ============================================================================

export interface CallFollowUpDatasource {
  searchCallFollowUps(request: CallFollowUpSearchRequest): Promise<CallFollowUpSearchResponse>;
  getSearchOptions(): Promise<CallFollowUpSearchOptionsResponse>;
}

// ============================================================================
// Datasource Implementation
// ============================================================================

export class CallFollowUpDatasourceImpl implements CallFollowUpDatasource {
  async searchCallFollowUps(request: CallFollowUpSearchRequest): Promise<CallFollowUpSearchResponse> {
    // Map domain request to API format
    const apiRequest = {
      page: request.page,
      per_page: request.per_page,
      search: request.search
        ? {
            text: request.search.text,
            column: request.search.column,
          }
        : undefined,
      filters: request.filters.map((f) => ({
        column: f.column,
        values: f.values,
        range: f.range
          ? {
              lower_bound: f.range.lower_bound,
              upper_bound: f.range.upper_bound,
            }
          : undefined,
      })),
      sorts: request.sorts.map((s) => ({
        column: s.column,
        order: s.order,
      })),
    };

    // v2 returns CallResponse items — map to CallFollowUpModel
    const response = await post<{ items: V2CallResponse[]; total: number; page: number; per_page: number }>(
      '/app/queue/follow-up/search',
      apiRequest
    );

    return {
      items: response.items.map(mapV2CallToFollowUp),
      total: response.total,
      page: response.page,
      per_page: response.per_page,
    };
  }

  async getSearchOptions(): Promise<CallFollowUpSearchOptionsResponse> {
    const response = await get<CallFollowUpSearchOptionsResponse>('/app/queue/follow-up/search');
    return response;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const callFollowUpDatasource: CallFollowUpDatasource = new CallFollowUpDatasourceImpl();
